import { getConfigTenant, isCorpTenant } from "@capone/common";
import { useEffect, useState } from "react";
import { CorpBusinessWithoutRewardsAccount, RewardsAccount } from "redmond";
import fetchBusinessPortalAuthorized from "../api/v1/portal-access/fetchBusinessPortalAuthorized";

interface UseFetchBusinessEligibilityParams {
  csrfToken: string;
  setEligibleBusinessAccounts: (accounts: RewardsAccount[]) => void;
  setBusinesses: (businesses: CorpBusinessWithoutRewardsAccount[]) => void;
  updateBusinessEligibility: (eligible: boolean | null) => void;
  enabled?: boolean;
}

export const useFetchBusinessEligibility = ({
  csrfToken,
  setEligibleBusinessAccounts,
  setBusinesses,
  updateBusinessEligibility,
  enabled = true,
}: UseFetchBusinessEligibilityParams) => {
  const resolutionKey = enabled && csrfToken ? csrfToken : "";
  const [resolvedKey, setResolvedKey] = useState(resolutionKey);
  const isResolved = !resolutionKey || resolvedKey === resolutionKey;

  useEffect(() => {
    const isCorporate = isCorpTenant(getConfigTenant());
    if (!resolutionKey) {
      setResolvedKey(resolutionKey);
      return;
    }

    if (!isCorporate) {
      const setBusinessEligibility = async function () {
        try {
          if (updateBusinessEligibility && setEligibleBusinessAccounts) {
            const eligibleAccountsResp = await fetchBusinessPortalAuthorized();
            if (eligibleAccountsResp.isAuthorized) {
              const eligibleAccounts = eligibleAccountsResp.rewardsAccounts;
              const { businesses: eligibleBusinesses } = eligibleAccountsResp;
              setEligibleBusinessAccounts(eligibleAccounts ?? []);
              setBusinesses(eligibleBusinesses);
              updateBusinessEligibility(eligibleAccounts.length > 0);
            } else {
              setEligibleBusinessAccounts([]);
              setBusinesses([]);
              updateBusinessEligibility(false);
            }
          }
        } catch {
          setEligibleBusinessAccounts([]);
          setBusinesses([]);
          updateBusinessEligibility(null);
        } finally {
          setResolvedKey(resolutionKey);
        }
      };
      setBusinessEligibility();
      return;
    }

    setResolvedKey(resolutionKey);
  }, [
    resolutionKey,
    setBusinesses,
    setEligibleBusinessAccounts,
    updateBusinessEligibility,
  ]);

  return { isResolved };
};
